import React from 'react';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import classNames from 'classnames';

import { RecipeRatings } from 'redux/modules/recipes/index.d';
import styles from './index.scss';

type RatingsBreakdownProp = Pick<RecipeRatings, 'totalRatings' | 'snapshot'>;

type BreakdownType = {
  [key: string]: {
    percentage: number;
    ratingCount: number;
  };
};

// Returns a reversed (5-1 Stars) object with a count and percentage against total ratings
const calculateRatingBreakdown = (snapshot: { [key: string]: number }, totalRatings: number) => {
  const breakdown: BreakdownType = {};

  Object.entries(snapshot)
    .reverse()
    .forEach(([key, ratingCount]) => {
      const percentage = Math.round((ratingCount / totalRatings) * 100);
      breakdown[key] = { percentage, ratingCount };
    });

  return breakdown;
};

const RatingsBreakdown = ({ snapshot, totalRatings }: RatingsBreakdownProp) => {
  if (!snapshot || !totalRatings) return null;

  const breakdownObj = calculateRatingBreakdown(snapshot, totalRatings);

  return (
    <div data-testid="ratings-breakdown" className={styles.breakdown}>
      {Object.entries(breakdownObj).map(([key, { percentage, ratingCount }]) => {
        const ratingNumber = key.slice(7);
        const noRating = ratingCount === 0;

        return (
          <div className={styles.wrapper} key={ratingNumber}>
            <div
              data-testid={`rating-${ratingNumber}`}
              className={classNames(styles.ratingInfo, {
                [styles.greyOutText]: noRating,
              })}
            >
              <Typography styleAs="paragraphHeading" noMargins>
                {ratingNumber} Stars
              </Typography>
              <Typography styleAs="paragraphHeading" noMargins data-testid="rating-count">
                {ratingCount}
              </Typography>
            </div>

            <div
              className={styles.outerBar}
              aria-label={`${percentage}%`}
              data-testid={`outerBar-${ratingNumber}`}
            >
              <div
                className={styles.innerBar}
                style={{ width: `${percentage}%` }}
                data-testid={`innerBar-${ratingNumber}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RatingsBreakdown;
